// src/Login.jsx
import React, { useState, useEffect } from "react";
import {
  signInWithEmailAndPassword,
  onAuthStateChanged,
  getAuth,
  signOut,
} from "firebase/auth";
import { db, trackEvent } from "./FirebaseConfig.js";
import { Navigate, Link } from "react-router-dom";
import {
  Input,
  Button,
  Form,
  message,
  Spin,
  Alert,
  Card,
  Typography,
  Space,
  Layout
} from "antd";
import {
  LoginOutlined,
  UserOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { useTranslation } from "react-i18next";
import LanguageSwitch from "./components/LanguageSwitch";

const { Title, Text } = Typography;

/* ---------- 同意チェック ---------- */
function CheckAgreement({ user }) {
  const [verified, setVerified] = useState(null);

  useEffect(() => {
    (async () => {
      const ref = doc(db, "users", user.uid);
      const snap = await getDoc(ref);
      const data = snap.data() || {};
      setVerified(data.agreeTerms && data.agreePrivacy ? "ok" : "terms");
    })();
  }, [user]);

  if (verified === "ok") return <Navigate to="/" />;
  if (verified === "terms") return <Navigate to="/terms" />;
  return <Spin size="large" />;
}

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

const Login = () => {
  const { t } = useTranslation();
  const authInstance = getAuth();

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);

  const [loading, setLoading] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const [user, setUser] = useState(null);
  const [reauthRequired, setReauthRequired] = useState(false);
  const { Header, Sider, Content, Footer } = Layout;

  useEffect(() => {
    trackEvent("case_study_view", {
      case_study_id: "login_page",
    });
  }, []);
  /* ---------- 30日チェック ---------- */
  useEffect(() => {
    const unsub = onAuthStateChanged(authInstance, async (currentUser) => {
      setAuthChecking(true);

      if (!currentUser) {
        setUser(null);
        setAuthChecking(false);
        return;
      }

      const last = currentUser.metadata?.lastSignInTime;
      const lastMs = last ? new Date(last).getTime() : 0;

      if (lastMs && Date.now() - lastMs > THIRTY_DAYS_MS) {
        setReauthRequired(true);
        await signOut(authInstance);
        setUser(null);
        setAuthChecking(false);
        return;
      }

      setUser(currentUser);
      setAuthChecking(false);
    });

    return () => unsub();
  }, [authInstance]);

  /* ---------- ログイン ---------- */
  const handleSubmit = async () => {
    setLoading(true);
    try {
      const cred = await signInWithEmailAndPassword(
        authInstance,
        loginEmail,
        loginPassword
      );

      await setDoc(
        doc(db, "users", cred.user.uid),
        { lastLoginAt: serverTimestamp() },
        { merge: true }
      );

      setReauthRequired(false);
      message.success(t("login.messages.success"));
    } catch {
      message.error(t("login.messages.failed"));
    } finally {
      setLoading(false);
    }
  };

  /* ---------- UI ---------- */
  if (authChecking) {
    return (
      <div className="center-screen">
        <Spin size="large" tip={t("login.loading")} />
      </div>
    );
  }

  if (user) return <CheckAgreement user={user} />;

  return (
    <div
      style={{
        minHeight: "100vh",
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundImage: `
      radial-gradient(
        circle at top right,
        rgba(255, 242, 0, 0.18),
        rgba(255, 242, 0, 0.04) 40%,
        rgba(255, 255, 255, 0) 70%
      ),
      url(/main-2048x972.png)
    `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >

      {/* オーバーレイ */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.75), rgba(255,255,255,0.55))",
          zIndex: 0,
        }}
      />

      <Card
        style={{
          width: 420,
          borderRadius: 12,
          boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
        }}
        bodyStyle={{ padding: 32 }}
      >
        <LanguageSwitch />

        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <div style={{ textAlign: "center" }}>
            <Title level={3} style={{ marginBottom: 4 }}>
              <div className="app-title">Stomata Scope</div>
            </Title>
            <Text type="secondary">
              {t("login.subtitle", { defaultValue: "Sign in to your account" })}
            </Text>
          </div>

          {reauthRequired && (
            <Alert
              type="warning"
              showIcon
              message={t("login.messages.sessionExpired")}
            />
          )}

          <Form layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              label={t("login.email")}
              name="email"
              rules={[
                {
                  required: true,
                  message: t("login.errors.emailRequired"),
                },
                {
                  type: "email",
                  message: t("form.invalidEmail"),
                },
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                size="large"
                placeholder="email@example.com"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
              />
            </Form.Item>

            <Form.Item
              label={t("login.password")}
              name="password"
              rules={[
                {
                  required: true,
                  message: t("login.errors.passwordRequired"),
                },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                size="large"
                visibilityToggle={{
                  visible: passwordVisible,
                  onVisibleChange: setPasswordVisible,
                }}
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
              />
            </Form.Item>

            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              icon={<LoginOutlined />}
              loading={loading}
            >
              {t("login.loginBtn")}
            </Button>
          </Form>

          <Space
            direction="vertical"
            size={12}
            style={{
              width: "100%",
              textAlign: "center",
              marginTop: 4,
            }}
          >
            <Link to="/ResetPassword">
              {t("login.forgotPassword")}
            </Link>

            <div
              style={{
                width: "100%",
                height: 1,
                background: "#f0f0f0",
                margin: "8px 0 4px",
              }}
            />
          </Space>
          <div>
            <Text
              type="secondary"
              style={{
                fontSize: 13,
                lineHeight: 1.6,
              }}
            >
              {t("login.guest.title")}
              <br />
              {t("login.guest.description")}
            </Text>
          </div>

          <Link
            to="/case-studies"
            style={{ width: "100%" }}
            onClick={() => {
              trackEvent("case_study_entry_click", {
                source: "login_page",
              });
            }}
          >
            <Button
              size="large"
              block
              style={{
                borderRadius: 8,
                fontWeight: 600,
              }}
            >
              {t("login.guest.caseStudies")}
            </Button>
          </Link>

          <a
            href="https://happy-quality.notion.site/15771c88a48380c39416e9fa1bb2199d"
            target="_blank"
            rel="noopener noreferrer"
            style={{ width: "100%" }}
            onClick={() => {
              trackEvent("demo_quote_click", {
                source: "login_page",
              });
            }}
          >
            <Button
              type="link"
              block
            >
              {t("login.guest.demoQuote")} →
            </Button>
          </a>
        </Space>
      </Card>

    </div>
  );
};

export default Login;
